import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';

/**
 * The one dialog every moderation step shares: a title, a hint and a reason
 * field. Reports, mutes, lifts, rejections — the backend records all of them
 * with a reason (§19), so the UI asks for one the same way everywhere.
 *
 * Keys, not strings, are passed in, and translated here: the host may open
 * this from inside a `@for` where no `t` is in reach. `optional` relaxes the
 * required reason (queue rejections). The host owns the request and reports
 * back through `busy` and `error`.
 */
@Component({
  selector: 'app-community-reason-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  template: `
    <ng-container *transloco="let t">
      <div class="modal fade show d-block" tabindex="-1" role="dialog" (keydown.escape)="cancel()">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ t(titleKey, titleParams) }}</h5>
              <button type="button" class="btn-close" [disabled]="busy" (click)="cancel()"></button>
            </div>
            <div class="modal-body">
              @if (hintKey) {
                <p class="text-muted small">{{ t(hintKey, titleParams) }}</p>
              }
              @if (error) {
                <div class="alert alert-danger py-2">{{ t(error) }}</div>
              }
              <label class="form-label">
                {{ t(labelKey) }}@if (!optional) { *}
              </label>
              <textarea class="form-control" rows="3" maxlength="2000" autofocus
                        [placeholder]="placeholderKey ? t(placeholderKey) : ''"
                        [ngModel]="reason()" (ngModelChange)="reason.set($event)"></textarea>
              <ng-content></ng-content>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" [disabled]="busy" (click)="cancel()">
                {{ t('community.threads.cancel') }}
              </button>
              <button type="button" class="btn" [class]="'btn ' + confirmClass" [disabled]="busy || (!optional && !reason().trim())" (click)="confirm()">
                @if (busy) { <span class="spinner-border spinner-border-sm me-1" role="status"></span> }
                {{ t(confirmKey) }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityReasonModalComponent {
  @Input({ required: true }) titleKey!: string;
  @Input() titleParams: Record<string, unknown> = {};
  @Input() hintKey: string | null = null;
  @Input() labelKey = 'community.moderation.reason';
  @Input() placeholderKey: string | null = 'community.moderation.reason_placeholder';
  @Input() confirmKey = 'community.moderation.confirm';
  @Input() confirmClass = 'btn-primary';
  /** Allow an empty reason (the backend treats it as absent). */
  @Input() optional = false;
  @Input() busy = false;
  /** Translation key of the last failure, shown inside the dialog. */
  @Input() error: string | null = null;

  @Output() confirmed = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  reason = signal('');

  confirm(): void {
    const reason = this.reason().trim();
    if (!reason && !this.optional) return;
    this.confirmed.emit(reason);
  }

  cancel(): void {
    if (this.busy) return;
    this.cancelled.emit();
  }
}
